/**
 * Realm change, made visible (L8): the realm (REALMS) the player stands in sets the extra afterimage ghosts on a
 * dash (`realmGhosts`, capped at 2: cheap on tier 0), and a rise plays a one-shot pixel burst (square rings and
 * motes, no beams) with the banner "ทะลวงสู่ <realm>". The standing aura follows the realm in data/auras REALM_AURAS.
 */
import { rankFor, REALMS, STAGES, type World } from '@nine/shared';
import { PIXEL } from '../labels';
import { registerRenderPass } from '../passes/registry';

const realmOf = (w: World): number => Math.floor(rankFor(w.progress.cultivationSpent).index / STAGES);

/** Extra ghosts on the player's moving skills: 0 below ยอดฝีมือชั้นสาม, 1 there, 2 from ชั้นหนึ่ง. */
export const realmGhosts = (w: World): number => Math.min(2, Math.floor(realmOf(w) / 2));

const LIFE = 2.2;
/** The banner's state, shared by the two passes (one Renderer). */
const banner = { t: LIFE, name: '' };
interface S {
  realm: number;
  t: number;
  x: number;
  y: number;
}

registerRenderPass({
  id: 'realmBurst',
  layer: 'world',
  order: 307,
  state: (): S => ({ realm: -1, t: LIFE, x: 0, y: 0 }),
  draw(f, s: S) {
    const r = realmOf(f.world);
    if (s.realm >= 0 && r > s.realm) {
      s.t = banner.t = 0;
      banner.name = REALMS[r]!.name;
    }
    s.realm = r;
    if (s.t >= LIFE) return;
    s.t += f.dt;
    const [px, py] = f.effects.pos(f.world.player);
    s.x = px + f.ox;
    s.y = py + f.oy;
    const k = Math.min(1, s.t / 0.9);
    const g = f.g;
    const rad = 6 + k * 46;
    g.globalAlpha = 1 - k;
    g.fillStyle = '#ffe9a0';
    // A square-stepped ring (pixel rules: hard pixels, no gradient).
    for (let i = 0; i < 32; i++) {
      const a = (i / 32) * Math.PI * 2;
      g.fillRect(Math.round(s.x + Math.cos(a) * rad), Math.round(s.y - 10 + Math.sin(a) * rad * 0.5), 1, 1);
    }
    g.fillStyle = '#fff6d8';
    for (let i = 0; i < 14; i++) {
      const a = i * 2.4;
      const h = k * (20 + (i % 5) * 9);
      g.fillRect(Math.round(s.x + Math.cos(a) * (4 + (i % 4) * 4)), Math.round(s.y - 4 - h), 1, 2);
    }
    g.globalAlpha = 1;
  },
});

registerRenderPass({
  id: 'realmBanner',
  layer: 'top',
  order: 777,
  draw(f) {
    if (banner.t >= LIFE) return;
    const k = banner.t / LIFE;
    const a = k < 0.1 ? k / 0.1 : k > 0.75 ? (1 - k) / 0.25 : 1;
    const mg = f.mg;
    mg.save();
    mg.globalAlpha = a;
    mg.textAlign = 'center';
    mg.textBaseline = 'middle';
    mg.font = `700 28px ${PIXEL}`;
    const x = Math.round(f.screenW / 2);
    const y = Math.round(f.screenH * 0.26);
    const t = `ทะลวงสู่ ${banner.name}`;
    mg.fillStyle = '#1a1208';
    for (const [dx, dy] of [[2, 0], [-2, 0], [0, 2], [0, -2]] as const) mg.fillText(t, x + dx, y + dy);
    mg.fillStyle = '#ffe9a0';
    mg.fillText(t, x, y);
    mg.restore();
  },
});
